<template>
  <div class="fc-wrap">
    <!-- Bubble -->
    <div v-if="!open" class="fc-bubble" @click="openChat">
      💬
      <span v-if="onlineCount > 0" class="fc-online">{{ onlineCount }}</span>
      <span v-if="unreadCount > 0" class="fc-badge">{{ unreadCount }}</span>
    </div>

    <!-- Window -->
    <div v-else class="fc-window">
      <div class="fc-header">
        <div class="fc-title">
          Chat
          <span v-if="currentChatUser" class="fc-sub">{{ currentChatUser.username }}</span>
        </div>
        <button class="fc-close" @click="closeChat" type="button">✕</button>
      </div>

      <!-- Online users -->
      <div class="fc-users">
        <div class="fc-users-title">Online ({{ onlineCount }})</div>

        <div class="fc-users-list">
          <button
              v-for="u in onlineUsersFiltered"
              :key="u.userId"
              class="fc-user"
              :class="{ active: Number(u.userId) === Number(currentChatUserId) }"
              @click="selectUser(u)"
              type="button"
          >
            <span class="fc-dot" />
            <span class="fc-user-name">{{ u.username }}</span>

            <!-- optional: unread pro user -->
            <span v-if="getUnreadForUser(u.userId) > 0" class="fc-user-badge">
              {{ getUnreadForUser(u.userId) }}
            </span>
          </button>

          <div v-if="onlineUsersFiltered.length === 0" class="fc-users-empty">
            Niemand online.
          </div>
        </div>
      </div>

      <!-- Messages -->
      <div class="fc-body" ref="body">
        <div v-if="!currentChatUserId" class="fc-empty">
          Wähle oben einen Online-User aus, um zu chatten.
        </div>

        <div v-else>
          <div v-if="messages.length === 0" class="fc-empty">
            Noch keine Nachrichten.
          </div>

          <div
              v-for="m in messages"
              :key="m.id || m.clientMsgId"
              class="fc-row"
              :class="{ me: Number(m.fromUserId) === Number(myUserId) }"
          >
            <div class="fc-msg">
              <div class="fc-text">{{ m.text }}</div>
              <div class="fc-meta">
                {{ formatTime(m.createdAt) }}
                <span class="fc-status">
                <span v-if="getMessageStatus(m) === 'sent'">✓</span>
                <span v-else-if="getMessageStatus(m) === 'delivered'">✓✓</span>
                <span v-else class="read">✓✓</span>
                </span>
              </div>
            </div>
          </div>

          <div v-if="typingFromCurrentUser" class="fc-typing">tippt…</div>
        </div>
      </div>

      <!-- Input -->
      <div class="fc-footer">
        <input
            v-model="draft"
            class="fc-input"
            placeholder="Nachricht…"
            :disabled="!currentChatUserId"
            @input="onInput"
            @keydown.enter.exact.prevent="send"
        />
        <button class="fc-send" :disabled="!canSend" @click="send" type="button">
          Senden
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import Vue from 'vue'
import Component from 'vue-class-component'

@Component({
  sockets: {
    chatOnlineUsers(payload) { this.onChatOnlineUsers(payload) },
    chatMessage(payload) { this.onChatMessage(payload) },
    chatTyping(payload) { this.onChatTyping(payload) },
    chatAck(payload) { this.onChatAck(payload) },
    chatDelivered(payload) { this.onChatDelivered(payload) },
    chatSeen(payload) { this.onChatSeen(payload) },
    chatHistory(payload) {this.onChatHistory(payload)},
  },
})
export default class FloatingChat extends Vue {
  open = false

  currentChatUserId = null
  onlineUsers = []

  // messages by user
  messagesByUser = {}   // { [otherId]: [msg...] }
  unreadByUser = {}     // { [otherId]: number }
  typingUsers = {}

  draft = ''
  typingTimeout = null

  // track outgoing message status by clientMsgId
  pendingByClientId = {} // { [clientMsgId]: { otherId, localId } }

  onChatHistory(payload) {
    if (!payload) return

    const withUserId = Number(payload.withUserId)
    const list = Array.isArray(payload.messages) ? payload.messages : []

    if (!this.messagesByUser[withUserId]) this.$set(this.messagesByUser, withUserId, [])

    // ersetzen (History ist “Source of truth” beim Öffnen)
    this.$set(this.messagesByUser, withUserId, list)

    this.$nextTick(() => this.scrollToBottom())
  }

  get myUserId() {
    return Number(this.$store?.state?.user?.UserId || 0)
  }

  get onlineUsersFiltered() {
    return (this.onlineUsers || []).filter(u => Number(u.userId) !== Number(this.myUserId))
  }

  get onlineCount() {
    return this.onlineUsersFiltered.length
  }

  get currentChatUser() {
    return this.onlineUsersFiltered.find(u => Number(u.userId) === Number(this.currentChatUserId)) || null
  }
  getMessageStatus(m) {
    if (m.readAt) return 'read'
    if (m.deliveredAt) return 'delivered'
    return 'sent'
  }

  get messages() {
    if (!this.currentChatUserId) return []
    return this.messagesByUser[this.currentChatUserId] || []
  }

  get unreadCount() {
    return Object.values(this.unreadByUser || {}).reduce((sum, n) => sum + (Number(n) || 0), 0)
  }

  get canSend() {
    return !!this.currentChatUserId && (this.draft || '').trim().length > 0
  }

  get typingFromCurrentUser() {
    if (!this.currentChatUserId) return false
    return !!this.typingUsers[this.currentChatUserId]
  }

  mounted() {
    // optional pull
    try { this.$socket.emit('chatGetOnlineUsers', {}) } catch (e) {}
  }

  // ===== unread helpers
  getUnreadForUser(userId) {
    return Number(this.unreadByUser[Number(userId)] || 0)
  }
  setUnreadForUser(userId, val) {
    this.$set(this.unreadByUser, Number(userId), Number(val) || 0)
  }
  incUnreadForUser(userId) {
    const id = Number(userId)
    const old = Number(this.unreadByUser[id] || 0)
    this.$set(this.unreadByUser, id, old + 1)
  }

  // ===== socket handlers
  onChatOnlineUsers(payload) {
    const list = Array.isArray(payload)
        ? payload
        : (payload && Array.isArray(payload.users) ? payload.users : [])
    this.onlineUsers = list
  }

  onChatTyping(payload) {
    if (!payload) return
    this.$set(this.typingUsers, Number(payload.fromUserId), !!payload.isTyping)
  }

  onChatMessage(payload) {
    if (!payload) return

    const me = Number(this.myUserId)
    const fromId = Number(payload.fromUserId)
    const toId = Number(payload.toUserId)
    const otherId = (fromId === me) ? toId : fromId

    if (!this.messagesByUser[otherId]) this.$set(this.messagesByUser, otherId, [])

    // 1) Wenn das unsere eigene Message ist und wir vorher local "sending" angelegt haben:
    if (payload.clientMsgId && this.pendingByClientId[payload.clientMsgId]) {
      const pending = this.pendingByClientId[payload.clientMsgId]
      const arr = this.messagesByUser[pending.otherId] || []
      const idx = arr.findIndex(m => m.clientMsgId === payload.clientMsgId)
      if (idx >= 0) {
        this.$set(arr, idx, { ...payload, status: 'sent' })
      } else {
        arr.push({ ...payload, status: 'sent' })
      }
      this.$delete(this.pendingByClientId, payload.clientMsgId)
    } else {
      // 2) Normal: Duplikate vermeiden per id
      const exists = this.messagesByUser[otherId].some(m => String(m.id) === String(payload.id))
      if (!exists) this.messagesByUser[otherId].push(payload)
    }

    const isActiveChat = this.open && Number(this.currentChatUserId) === Number(otherId)
    if (isActiveChat) {
      this.$nextTick(() => this.scrollToBottom())
      this.markRead(otherId)
      this.setUnreadForUser(otherId, 0)
    } else {
      // unread nur wenn es von "anderen" kommt
      if (fromId !== me) this.incUnreadForUser(otherId)
    }
  }

  onChatAck(payload) {
    // payload sollte mindestens: { clientMsgId, id, createdAt }
    if (!payload || !payload.clientMsgId) return
    const pending = this.pendingByClientId[payload.clientMsgId]
    if (!pending) return

    const arr = this.messagesByUser[pending.otherId] || []
    const idx = arr.findIndex(m => m.clientMsgId === payload.clientMsgId)
    if (idx >= 0) {
      this.$set(arr, idx, { ...arr[idx], status: 'sent', id: payload.id || arr[idx].id, createdAt: payload.createdAt || arr[idx].createdAt })
    }
  }

  onChatDelivered(payload) {
    // payload: { id, toUserId, fromUserId, deliveredAt }
    if (!payload || !payload.id) return
    const msgId = payload.id
    const deliveredAt = payload.deliveredAt

    Object.values(this.messagesByUser).forEach(list => {
      const msg = list.find(m => m.id === msgId)
      if (msg) msg.deliveredAt = deliveredAt
    })
    this.patchMessageById(payload.id, (m) => ({ ...m, deliveredAt: payload.deliveredAt || new Date().toISOString(), status: 'delivered' }))
  }

  onChatSeen(payload) {
    // payload: { withUserId, fromUserId, readAt }
    // optional: wenn du später "seen" anzeigen willst
  }

  patchMessageById(id, mapper) {
    const keys = Object.keys(this.messagesByUser || {})
    for (let i = 0; i < keys.length; i++) {
      const k = keys[i]
      const arr = this.messagesByUser[k] || []
      const idx = arr.findIndex(m => String(m.id) === String(id))
      if (idx >= 0) {
        this.$set(arr, idx, mapper(arr[idx]))
        return
      }
    }
  }

  // ===== UI actions
  openChat() {
    this.open = true
    this.$nextTick(() => this.scrollToBottom())
    if (this.currentChatUserId) {
      this.setUnreadForUser(this.currentChatUserId, 0)
      this.markRead(this.currentChatUserId)
    }
  }

  closeChat() {
    this.open = false
  }

  selectUser(u) {
    if (!u || !u.userId) return
    this.currentChatUserId = Number(u.userId)
    this.setUnreadForUser(this.currentChatUserId, 0)
    this.$set(this.typingUsers, this.currentChatUserId, false)
    this.markRead(this.currentChatUserId)
    this.$socket.emit('chatHistory', { withUserId: this.currentChatUserId, limit: 80 })
    this.$nextTick(() => this.scrollToBottom())
  }

  // ===== chat logic
  onInput() {
    if (!this.currentChatUserId) return
    this.$socket.emit('chatTyping', { toUserId: this.currentChatUserId, isTyping: true })

    clearTimeout(this.typingTimeout)
    this.typingTimeout = setTimeout(() => {
      this.$socket.emit('chatTyping', { toUserId: this.currentChatUserId, isTyping: false })
    }, 900)
  }

  send() {
    if (!this.canSend) return

    const text = (this.draft || '').trim()
    const toUserId = Number(this.currentChatUserId)
    const clientMsgId = 'c_' + Date.now() + '_' + Math.random().toString(16).slice(2)

    // local "sending" (damit du Status anzeigen kannst)
    if (!this.messagesByUser[toUserId]) this.$set(this.messagesByUser, toUserId, [])
    this.messagesByUser[toUserId].push({
      id: null,
      clientMsgId,
      fromUserId: this.myUserId,
      toUserId,
      text,
      createdAt: new Date().toISOString(),
      status: 'sending',
      deliveredAt: null,
      readAt: null,
    })
    this.$set(this.pendingByClientId, clientMsgId, { otherId: toUserId })

    // emit -> server soll clientMsgId zurückgeben in chatAck + chatMessage
    this.$socket.emit('chatSend', { toUserId, text, clientMsgId })

    this.draft = ''
    this.$socket.emit('chatTyping', { toUserId, isTyping: false })
    this.$nextTick(() => this.scrollToBottom())
  }

  markRead(withUserId) {
    try { this.$socket.emit('chatMarkRead', { withUserId: Number(withUserId) }) } catch (e) {}
  }

  // ===== helpers
  scrollToBottom() {
    const el = this.$refs.body
    if (el && el.scrollHeight != null) el.scrollTop = el.scrollHeight
  }

  formatTime(dt) {
    try {
      const d = new Date(dt)
      return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    } catch (e) {
      return ''
    }
  }

  renderStatus(m) {
    // sending → …
    if (m.status === 'sending') return ' …'
    // deliveredAt → ✓✓
    if (m.deliveredAt) return ' ✓✓'
    // sent → ✓
    return ' ✓'
  }
}
</script>

<style scoped>
.fc-wrap { position: fixed; right: 16px; bottom: 90px; z-index: 99999; font-family: inherit; }
.fc-bubble { position: relative; width: 54px; height: 54px; border-radius: 999px; background: #2d5bff; color: white; display: flex; align-items: center; justify-content: center; cursor: pointer; box-shadow: 0 10px 30px rgba(0,0,0,.2); user-select: none; font-size: 22px; }
.fc-badge { position: absolute; right: -6px; top: -6px; background: #ff3b30; color: white; border-radius: 999px; padding: 2px 7px; font-size: 12px; font-weight: 600; }
.fc-online { position: absolute; left: -6px; bottom: -6px; min-width: 18px; height: 18px; padding: 0 5px; background: #22c55e; color: white; border-radius: 999px; font-size: 11px; font-weight: 700; display: flex; align-items: center; justify-content: center; box-shadow: 0 2px 6px rgba(0,0,0,.25); }
.fc-window { width: 360px; height: 460px; background: white; border-radius: 14px; box-shadow: 0 10px 30px rgba(0,0,0,.25); overflow: hidden; display: flex; flex-direction: column; }
.fc-header { height: 52px; display: flex; align-items: center; justify-content: space-between; padding: 0 12px; background: #111827; color: white; }
.fc-title { font-weight: 700; }
.fc-sub { font-weight: 400; opacity: .75; margin-left: 6px; }
.fc-close { background: transparent; border: 0; color: white; font-size: 18px; cursor: pointer; }
.fc-users { padding: 10px 12px; border-bottom: 1px solid rgba(0,0,0,.06); background: #fff; }
.fc-users-title { font-size: 12px; font-weight: 700; opacity: .75; margin-bottom: 8px; }
.fc-users-list { display: flex; gap: 8px; overflow-x: auto; padding-bottom: 4px; }
.fc-users-empty { font-size: 12px; opacity: .6; }
.fc-user { display: inline-flex; align-items: center; gap: 6px; border: 1px solid rgba(0,0,0,.12); background: #f8fafc; padding: 6px 10px; border-radius: 999px; cursor: pointer; white-space: nowrap; }
.fc-user.active { border-color: rgba(45,91,255,.6); background: rgba(45,91,255,.08); }
.fc-dot { width: 8px; height: 8px; border-radius: 99px; background: #22c55e; box-shadow: 0 0 0 3px rgba(34,197,94,.12); }
.fc-user-name { font-size: 13px; }
.fc-user-badge { margin-left: 6px; background:#ff3b30; color:#fff; border-radius:999px; padding:0 6px; font-size:11px; font-weight:700; }
.fc-body { flex: 1; padding: 12px; overflow: auto; background: #f5f7fb; }
.fc-empty { opacity: .65; font-size: 13px; text-align: center; margin-top: 24px; }
.fc-row { display: flex; margin-bottom: 8px; }
.fc-row.me { justify-content: flex-end; }
.fc-msg { max-width: 78%; background: white; border-radius: 12px; padding: 8px 10px; box-shadow: 0 2px 10px rgba(0,0,0,.06); }
.fc-row.me .fc-msg { background: #2d5bff; color: white; }
.fc-text { font-size: 14px; white-space: pre-wrap; word-break: break-word; }
.fc-meta { margin-top: 2px; font-size: 11px; opacity: .8; display:flex; gap:8px; justify-content:flex-end; }
.fc-status {  margin-left: 6px;  font-size: 11px;  opacity: .7;}
.fc-status .read {color: #4f8cff;opacity: 1;}
.fc-typing { font-size: 12px; opacity: .7; margin-top: 8px; }
.fc-footer { display: flex; gap: 8px; padding: 10px; background: white; border-top: 1px solid rgba(0,0,0,.06); }
.fc-input { flex: 1; border: 1px solid rgba(0,0,0,.15); border-radius: 10px; padding: 8px 10px; outline: none; }
.fc-input:disabled { opacity: .7; background: #f3f4f6; }
.fc-send { border: 0; border-radius: 10px; padding: 0 12px; background: #111827; color: white; cursor: pointer; }
.fc-send:disabled { opacity: .5; cursor: not-allowed; }
</style>
